import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getConfig } from '../lib/config';
import * as authApi from '../api/auth';
import { ErrorAlert, Field } from '../components/ui';
import { safeRedirectPath } from '../lib/safePath';
import { cn, ui } from '../lib/ui';

const OAUTH_PROVIDER_NAMES = {
  google: 'Google',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  microsoft: 'Microsoft',
  'microsoft-entra': 'Microsoft Entra ID',
  entra: 'Microsoft Entra ID',
};

export default function Login() {
  const { login, completeMfaLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { productName, oauthProviders = [] } = getConfig();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // MFA challenge state — set when the password stage returns mfaRequired.
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  // `from` is ProtectedRoute's copy of the current pathname, which the link
  // a visitor followed shapes. Only internal paths go to navigate() (SEC-27).
  const destination = safeRedirectPath(location.state && location.state.from);

  async function onSubmit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // The API accepts username OR email + password.
      const credentials = identifier.includes('@')
        ? { email: identifier, password }
        : { username: identifier, password };
      const result = await login(credentials);
      if (result.mfaRequired) {
        // No session yet — switch to the challenge step. The token is only
        // valid for 5 minutes.
        setMfaToken(result.mfaToken);
      } else {
        navigate(destination, { replace: true });
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function startOauth(provider) {
    setError(null);
    setBusy(true);
    try {
      // The server answers 400 for providers without configured credentials —
      // that error is surfaced as-is rather than hiding the button.
      const { authorizationUrl } = await authApi.oauthInitiate(provider);
      window.location.assign(authorizationUrl);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  async function onSubmitMfa(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await completeMfaLogin(mfaToken, useRecovery ? { recoveryCode: code } : { code });
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (mfaToken) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <div className={ui.authBrand}>
            <h1 className={ui.h1}>{productName}</h1>
            <p className={cn(ui.muted, 'text-body-s')}>Two-factor authentication</p>
          </div>
          <ErrorAlert error={error} />
          <form onSubmit={onSubmitMfa}>
            <Field
              label={useRecovery ? 'Recovery code' : 'Authenticator code'}
              type="text"
              inputMode={useRecovery ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              autoFocus
              required
              placeholder={useRecovery ? 'e.g. 3f9a2c81d4' : '6-digit code'}
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            <button className={cn(ui.btnPrimary, 'w-full')} type="submit" disabled={busy}>
              {busy ? 'Verifying…' : 'Verify and sign in'}
            </button>
          </form>
          <p className="text-body-s mt-4">
            <button
              type="button"
              className={ui.btnSecondary}
              onClick={() => {
                setUseRecovery(v => !v);
                setCode('');
                setError(null);
              }}
            >
              {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
            </button>
          </p>
          <p className={cn(ui.muted, 'text-body-s')}>
            The challenge expires after 5 minutes —{' '}
            <button
              type="button"
              className={ui.btnSecondary}
              onClick={() => {
                setMfaToken(null);
                setCode('');
                setError(null);
              }}
            >
              start over
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <div className={ui.authBrand}>
          <h1 className={ui.h1}>{productName}</h1>
          <p className={cn(ui.muted, 'text-body-s')}>Sign in to your investor account</p>
        </div>
        <ErrorAlert error={error} />
        <form onSubmit={onSubmit}>
          <Field
            label="Email or username"
            type="text"
            autoComplete="username"
            required
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
          />
          <Field
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button className={cn(ui.btnPrimary, 'w-full')} type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {oauthProviders.length > 0 && (
          <div className="mt-4">
            <p className={cn(ui.muted, 'text-body-s')}>Or continue with</p>
            <div className={ui.row} data-testid="oauth-providers">
              {oauthProviders.map(provider => (
                <button
                  key={provider}
                  type="button"
                  className={ui.btnSecondary}
                  disabled={busy}
                  onClick={() => startOauth(provider)}
                >
                  {OAUTH_PROVIDER_NAMES[provider] || provider}
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="text-body-s mt-4">
          New to the platform? <Link to="/register">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
